import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { CheckboxV2, Icon, Dialog, DialogContent, DialogHeader, IconButton } from '@shopflo/ui'
import { Cube, X, ArrowLeft } from '@phosphor-icons/react'
import { useLocale } from 'src/lib/hooks/useLocal'
import { ProductSearchDialogProps, ProductSelectorViewMode } from './lib/productSelectorTypes'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import ProductSelectorContent from './ProductSelectorContent'
import ExclusionList from './ExclusionList'
import { ProductIcon } from 'src/components/discounts/v2/common/ProductIcons'

const ProductSearchDialog: React.FC<ProductSearchDialogProps> = ({
  openHandler: { open, onOpenChange },
  actionHandler: { onApply },
  products: { initialSelectedProducts, initialExcludedProducts },
  catalogueType,
  viewModeHandler: { defaultViewMode, showExclusionOption, showAllOption = true },
  excludedStatuses,
  disabledVariants,
  singleSelect = false,
  includeBundleProducts = true
}) => {
  const { t } = useLocale()

  const [viewMode, setViewMode] = useState<ProductSelectorViewMode>(ProductSelectorViewMode.SELECT)
  const [isAllSelected, setIsAllSelected] = useState(false)
  const [exclusions, setExclusions] = useState<CatalogueDataType[]>([])

  useEffect(() => {
    if (open) {
      setViewMode(defaultViewMode ?? ProductSelectorViewMode.SELECT)
      const isAll =
        showAllOption &&
        (initialSelectedProducts ?? []).some(
          product => product.id === 'shopflo-all-products' || product.id === 'shopflo-all-collections'
        )
      setIsAllSelected(!!isAll)
      setExclusions(initialExcludedProducts ?? [])
    }
  }, [open, initialSelectedProducts, initialExcludedProducts, defaultViewMode, showAllOption])

  const handleApplySelection = (selected: CatalogueDataType[]) => {
    if (isAllSelected) {
      const allProductsPlaceholder: CatalogueDataType =
        catalogueType === 'COLLECTION'
          ? {
              isVariant: false,
              id: 'shopflo-all-collections',
              description: t('product_search_dialog.all_collections')
            }
          : {
              isVariant: false,
              id: 'shopflo-all-products',
              description: t('product_search_dialog.all_products')
            }

      const selectedWithExclusions = [allProductsPlaceholder]
      onApply(selectedWithExclusions, exclusions.length > 0 ? exclusions : undefined)
    } else {
      onApply(selected)
    }
    onOpenChange(false)
  }

  const handleApplyExclusion = (selectedExclusions: CatalogueDataType[]) => {
    setExclusions(selectedExclusions)
    setViewMode(ProductSelectorViewMode.SELECT)
  }

  const renderHeader = () => {
    if (viewMode === ProductSelectorViewMode.EXCLUDE) {
      return (
        <div className='flex gap-3 items-center'>
          <IconButton
            IconComponent={ArrowLeft}
            size='md'
            onClick={() => setViewMode(ProductSelectorViewMode.SELECT)}
            variant='outline'
            className='mr-1'
          />
          <h3 className='text-text-lg-semibold'>
            {catalogueType === 'COLLECTION'
              ? t('product_search_dialog.exclude_collections')
              : t('product_search_dialog.exclude_products')}
          </h3>
        </div>
      )
    }

    return (
      <div className='flex gap-3 items-center'>
        <div className='h-10 w-10 rounded-xl flex items-center justify-center bg-zinc-100'>
          <Icon IconComponent={Cube} size='lg' className='text-zinc-500' iconProps={{ weight: 'duotone' }} />
        </div>
        <h3 className='text-text-lg-semibold'>
          {catalogueType === 'COLLECTION'
            ? t('product_search_dialog.add_collections')
            : t('product_search_dialog.add_products')}
        </h3>
      </div>
    )
  }

  const renderContent = () => {
    return (
      <AnimatePresence initial={false}>
        {viewMode === ProductSelectorViewMode.SELECT ? (
          <motion.div
            key='select-mode'
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className='absolute inset-0 h-full w-full bg-white'
          >
            <ProductSelectorContent
              catalogueType={catalogueType}
              initialSelectedProducts={
                isAllSelected
                  ? []
                  : (initialSelectedProducts ?? []).filter(
                      product => product.id !== 'shopflo-all-products' && product.id !== 'shopflo-all-collections'
                    )
              }
              onApply={handleApplySelection}
              onCancel={() => onOpenChange(false)}
              submitLabel={t('product_search_dialog.add')}
              hideProductList={isAllSelected}
              excludedStatuses={excludedStatuses}
              disabledVariants={disabledVariants}
              singleSelect={singleSelect}
              includeBundleProducts={includeBundleProducts}
            >
              {showAllOption && (
                <div className='grid grid-cols-[3fr_1fr_1fr] gap-4 pb-1 items-center'>
                  <div className='flex items-center gap-3'>
                    <CheckboxV2
                      size='sm'
                      checked={isAllSelected}
                      onCheckedChange={checked => setIsAllSelected(!!checked)}
                    />
                    <ProductIcon wrapperSize='md' />
                    <span className='text-text-sm-medium text-zinc-800'>
                      {catalogueType === 'COLLECTION'
                        ? t('product_search_dialog.all_collections')
                        : t('product_search_dialog.all_products')}
                    </span>
                  </div>
                </div>
              )}

              <AnimatePresence initial={false} mode='wait'>
                {showExclusionOption && isAllSelected && (
                  <motion.div
                    key='exclude-option'
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className='overflow-hidden flex-1'
                  >
                    <ExclusionList
                      exclusions={exclusions}
                      catalogueType={catalogueType}
                      onRemoveExclusion={(index: number) => {
                        const newExclusions = [...exclusions]
                        newExclusions.splice(index, 1)
                        setExclusions(newExclusions)
                      }}
                      onSwitchToExcludeMode={() => setViewMode(ProductSelectorViewMode.EXCLUDE)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </ProductSelectorContent>
          </motion.div>
        ) : (
          <motion.div
            key='exclude-mode'
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className='absolute inset-0 h-full w-full'
          >
            <ProductSelectorContent
              catalogueType={catalogueType}
              initialSelectedProducts={exclusions}
              onApply={handleApplyExclusion}
              onCancel={() => setViewMode(ProductSelectorViewMode.SELECT)}
              submitLabel={
                catalogueType === 'COLLECTION'
                  ? t('product_search_dialog.add_exclusion_collections')
                  : t('product_search_dialog.add_exclusion')
              }
              excludedStatuses={excludedStatuses}
            />
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-[600px] bg-white text-black z-[50] !rounded-2xl p-0 gap-0 overflow-hidden'>
        <DialogHeader>
          <div className='px-5 pt-4 flex items-center'>
            {renderHeader()}

            <IconButton
              IconComponent={X}
              size='sm'
              onClick={() => onOpenChange(false)}
              className='ml-auto'
              variant='ghost'
            />
          </div>
        </DialogHeader>

        <div className='h-[600px] overflow-y-auto overflow-x-hidden relative'>{renderContent()}</div>
      </DialogContent>
    </Dialog>
  )
}

export default ProductSearchDialog
